/**
 * GroupCard component
 * Displays group card with image, name, member count (icon), and optional my position.
 * Shows a small gamified "Creator" badge when the current user created the group.
 */
import React, { useMemo } from 'react'
import { View, Text, GestureResponderEvent } from 'react-native'
import { Users, Trophy, Crown } from 'lucide-react-native'
import { BaseCard } from '../BaseCard'
import { DEFAULT_GROUP_IMAGE_SOURCE } from '../../../constants/assets'
import { useTheme } from '../../../src/theme/useTheme'
import { GROUP_CARD_CREATOR_LABEL } from '../../../src/friends/constants/groups.constants'
import { getGroupCardStyles, GROUP_CARD_SUBTITLE_ICON_SIZE, GROUP_CARD_RANK_GOLD } from './GroupCard.styles'
import type { Group } from '../../../src/friends/types/groups.types'

export interface GroupCardProps {
  group: Group
  /** When true, shows a small Creator badge (icon + label) on the card. */
  isCreator?: boolean
  /** Current user's rank in this group (1-based). When set, shown with medal icon below member count. */
  myPosition?: number
  cardWidth?: number
  onPress?: () => void
  isSelected?: boolean
  onRemoveGroup?: (groupId: string, group?: Group) => void
  testID?: string
}

export const GroupCard: React.FC<GroupCardProps> = ({
  group,
  isCreator = false,
  myPosition,
  cardWidth = 160,
  onPress,
  isSelected = false,
  onRemoveGroup,
  testID,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getGroupCardStyles(theme), [theme])

  const handleRemove = (e: GestureResponderEvent) => {
    e.stopPropagation()
    onRemoveGroup?.(group.id, group)
  }

  const memberCount = group.memberCount ?? 0
  const showPosition = myPosition != null && myPosition > 0
  const subtitle = (
    <View style={styles.subtitleRow}>
      <View style={styles.subtitleRow}>
        <Users
          size={GROUP_CARD_SUBTITLE_ICON_SIZE}
          color={theme.gray[600]}
          style={styles.subtitleIcon}
        />
        <Text style={styles.subtitleText}>{memberCount}</Text>
      </View>
      {showPosition && (
        <>
          <Text style={styles.subtitleSeparator}>·</Text>
          <View style={styles.subtitleRow}>
            <Trophy
              size={GROUP_CARD_SUBTITLE_ICON_SIZE}
              color={GROUP_CARD_RANK_GOLD}
              style={styles.subtitleIcon}
            />
            <Text style={[styles.subtitleText, styles.subtitleRankText]}>#{myPosition}</Text>
          </View>
        </>
      )}
    </View>
  )

  const badge =
    isCreator ? (
      <View style={styles.creatorBadge}>
        <Crown
          size={14}
          color="#1A202C"
          style={styles.creatorBadgeIcon}
        />
        <Text style={styles.creatorBadgeText}>{GROUP_CARD_CREATOR_LABEL}</Text>
      </View>
    ) : undefined

  return (
    <BaseCard
      title={group.name}
      subtitle={subtitle}
      badge={badge}
      cardWidth={cardWidth}
      imageSource={group.imageUrl ? { uri: group.imageUrl } : DEFAULT_GROUP_IMAGE_SOURCE}
      onRemove={onRemoveGroup ? handleRemove : undefined}
      onPress={onPress ?? (() => undefined)}
      showSelectionIndicator={isSelected}
      testID={testID || `group-card-${group.id}`}
    />
  )
}

